import { ReactWidget } from '@theia/core/lib/browser';
import { DisposableCollection } from '@theia/core/lib/common/disposable';
import { inject, injectable, postConstruct } from '@theia/core/shared/inversify';
import { EditorManager, EditorWidget } from '@theia/editor/lib/browser';
import * as monaco from '@theia/monaco-editor-core';
import type { ComponentType } from 'react';
import type { MonacoBackedEditor } from '../../editor/types';

type MonacoColorClickContribution = {
    onMouseDown?: (event: unknown) => void;
};

type UiModule = Pick<typeof import('@lved/ui'), 'ColorPicker' | 'Textarea'>;

type EditorUiHostProps = {
    editor: monaco.editor.IStandaloneCodeEditor | null;
    cursorPositionChangeEvent: monaco.editor.ICursorPositionChangedEvent | null;
};

@injectable()
export class EditorUiHostWidget extends ReactWidget {
    static readonly ID: string = 'widget:lved-editor-ui-host';
    static readonly LABEL: string = 'Editor UI Host';

    protected module: UiModule | undefined;
    protected loadError: string | undefined;
    protected activeEditorListener: DisposableCollection = new DisposableCollection();
    protected activeEditorControl: monaco.editor.IStandaloneCodeEditor | null = null;
    protected cursorPositionChangeEvent: monaco.editor.ICursorPositionChangedEvent | null = null;
    protected patchedColorContribution:
        | {
              contribution: MonacoColorClickContribution;
              onMouseDown: ((event: unknown) => void) | undefined;
          }
        | undefined;

    @inject(EditorManager)
    protected readonly editorManager!: EditorManager;

    @postConstruct()
    protected init(): void {
        this.doInit();
    }

    protected async doInit(): Promise<void> {
        this.id = EditorUiHostWidget.ID;
        this.title.label = EditorUiHostWidget.LABEL;
        this.title.caption = EditorUiHostWidget.LABEL;
        this.title.closable = false;
        this.node.style.display = 'none';
        this.toDispose.push({ dispose: () => this.activeEditorListener.dispose() });
        this.toDispose.push(
            this.editorManager.onCurrentEditorChanged((editor: EditorWidget | undefined) => {
                this.attachActiveEditorListener(editor);
            })
        );
        await this.loadModule();
        this.attachActiveEditorListener(this.editorManager.currentEditor);
        this.update();
    }

    protected async loadModule(): Promise<void> {
        try {
            this.module = (await import('@lved/ui')) as unknown as UiModule;
            this.loadError = undefined;
        } catch (error: unknown) {
            this.loadError = error instanceof Error ? error.message : 'Failed to load LVED editor UI host module.';
        }
    }

    protected attachActiveEditorListener(editor: EditorWidget | undefined): void {
        this.activeEditorListener.dispose();
        this.activeEditorListener = new DisposableCollection();
        this.activeEditorControl = null;
        this.cursorPositionChangeEvent = null;
        this.restoreMonacoColorClickContribution();

        const xmlPath: string | undefined = editor?.getResourceUri()?.path.toString();
        if (!editor?.editor || !xmlPath || !xmlPath.toLowerCase().endsWith('.xml')) {
            this.update();
            return;
        }

        this.activeEditorControl = (editor.editor as unknown as MonacoBackedEditor).getControl?.() ?? null;
        this.activeEditorControl?.updateOptions({
            colorDecorators: true,
            colorDecoratorsActivatedOn: 'click'
        });
        this.disableMonacoColorClickContribution();

        const editorDomNode: HTMLElement | null = this.activeEditorControl?.getDomNode() ?? null;
        if (editorDomNode) {
            const stopDefaultColorPicker = (event: MouseEvent): void => {
                const target: HTMLElement | null = event.target as HTMLElement | null;
                if (!target?.closest('.colorpicker-color-decoration')) return;
                event.preventDefault();
                event.stopPropagation();
            };
            editorDomNode.addEventListener('mousedown', stopDefaultColorPicker, true);
            editorDomNode.addEventListener('click', stopDefaultColorPicker, true);
            this.activeEditorListener.push({
                dispose: () => {
                    editorDomNode.removeEventListener('mousedown', stopDefaultColorPicker, true);
                    editorDomNode.removeEventListener('click', stopDefaultColorPicker, true);
                }
            });
        }

        if (this.activeEditorControl) {
            this.activeEditorListener.push(
                this.activeEditorControl.onDidChangeCursorPosition((event) => {
                    this.cursorPositionChangeEvent = event;
                    this.update();
                })
            );
        }

        this.update();
    }

    protected disableMonacoColorClickContribution(): void {
        const contribution: MonacoColorClickContribution | null | undefined = this.activeEditorControl?.getContribution(
            'editor.contrib.colorContribution'
        ) as MonacoColorClickContribution | null | undefined;
        if (!contribution || this.patchedColorContribution?.contribution === contribution) return;

        this.patchedColorContribution = {
            contribution,
            onMouseDown: contribution.onMouseDown
        };
        contribution.onMouseDown = () => undefined;
        this.activeEditorListener.push({
            dispose: () => this.restoreMonacoColorClickContribution()
        });
    }

    protected restoreMonacoColorClickContribution(): void {
        if (!this.patchedColorContribution) return;
        this.patchedColorContribution.contribution.onMouseDown = this.patchedColorContribution.onMouseDown;
        this.patchedColorContribution = undefined;
    }

    protected render(): JSX.Element {
        if (this.loadError || !this.module) return <></>;
        const ColorPicker = this.module.ColorPicker as unknown as ComponentType<EditorUiHostProps>;
        const Textarea = this.module.Textarea as unknown as ComponentType<EditorUiHostProps>;

        return (
            <>
                <ColorPicker
                    editor={this.activeEditorControl}
                    cursorPositionChangeEvent={this.cursorPositionChangeEvent}
                />
                <Textarea
                    editor={this.activeEditorControl}
                    cursorPositionChangeEvent={this.cursorPositionChangeEvent}
                />
            </>
        );
    }
}
